import http from "@/services/http";
import { getSourceId } from "@/utils/constants";

// AI 平台维度的品牌指标趋势 - 单品牌在各 AI 平台上的趋势

export interface BrandAiTrendPoint {
  date: string; // YYYY-MM-DD
  platform: string; // 平台标识（如 deepseek / doubao / ...）
  mentionRate: number; // 提及率（%）
  firstMentionRate?: number;
  firstRecommendationRate?: number;
}

export interface BrandAiTrendResponse {
  code: number;
  message: string;
  data: BrandAiTrendPoint[] | Record<string, BrandAiTrendPoint[]>; // 兼容两种结构
}

// 新的字典结构：按指标类型分组，每个指标下是各 AI 平台的时间序列
export interface BrandAiTrendSeriesByPlatform {
  aiId: string;
  aiName: string;
  series: Array<{ day: string; rate: number }>;
}

export type BrandAiTrendDict = Record<string, BrandAiTrendSeriesByPlatform[]>;

// 纯字典：{ [metricType]: { [aiId]: Array<{ day, rate }> } }
export type BrandAiTrendPureDict = Record<
  string,
  Record<string, Array<{ day: string; rate: number }>>
> & {
  __aiNameMap?: Record<string, string>;
};

export interface BrandAiTrendParams {
  brandId: string;
  categoryId: string;
  days?: number;
  startDay?: string; // 自定义开始日期，格式：2025-12-30
  endDay?: string; // 自定义结束日期，格式：2025-12-30
  metricType?: string;
  modelId?: string;
  qId?: string; // 问题ID
  sourceId?: number; // 数据源ID：2=国内，3=国外
}

export const brandAiMetricsTrendApi = {
  async getAiTrend(params: BrandAiTrendParams): Promise<BrandAiTrendPureDict> {
    try {
      // 自动添加sourceId，如果没有指定则使用默认值
      const sourceId = params.sourceId ?? getSourceId();

      const query: Record<string, string> = {
        brandId: params.brandId,
        categoryId: params.categoryId,
        days: String(params.days ?? 7),
        metricType:
          params.metricType ??
          "mentionRate,firstMentionRate,firstRecommendationRate",
        sourceId: sourceId.toString(),
      };

      // 添加可选参数
      if (params.startDay) {
        query.startDay = params.startDay;
      }
      if (params.endDay) {
        query.endDay = params.endDay;
      }
      if (params.qId) {
        query.qId = params.qId;
      }

      const urlParams = new URLSearchParams(query);
      const res = await http.get<any>(
        `data/brand/metrics/ai-trend?${urlParams.toString()}`
      );
      if (!res || !res.data) return {};
      const payload = res.data;

      if (!(payload && payload.data)) return {};
      const data = payload.data;
      const rateList = data.rateList || {};
      const aiNameMap: Record<string, string> = {};
      if (Array.isArray(data.aiModelList)) {
        for (const m of data.aiModelList as Array<{
          aiId: string;
          aiName: string;
        }>) {
          if (m && m.aiId) aiNameMap[m.aiId] = m.aiName || m.aiId;
        }
      }

      const result: BrandAiTrendPureDict = {};
      Object.keys(rateList).forEach((metricType: string) => {
        const byPlatform: Record<
          string,
          Array<{ day: string; rate: number }>
        > = rateList[metricType] || {};
        const dictByAi: Record<
          string,
          Array<{ day: string; rate: number }>
        > = {};

        Object.keys(byPlatform).forEach((aiId: string) => {
          const rawSeries = byPlatform[aiId] || [];
          dictByAi[aiId] = rawSeries.map(({ day, rate }) => {
            const v = typeof rate === "number" ? rate : Number(rate) || 0;
            const percent = v <= 1 ? v * 100 : v;
            return { day, rate: percent };
          });
        });

        result[metricType] = dictByAi;
      });
      // 附带 AI 平台名称映射，供前端展示用
      result.__aiNameMap = aiNameMap;
      return result;
    } catch (e) {
      console.error("brandAiMetricsTrend API error:", e);
      return {};
    }
  },
};

export default brandAiMetricsTrendApi;
